/**
 * Three Column Images Carousel
 * Handles mobile carousel functionality with swipe support
 */

class ThreeColumnCarousel {
  constructor(section) {
    this.section = section;
    this.grid = section.querySelector('.three-column-grid');
    this.items = section.querySelectorAll('.column-item');
    this.prevButton = section.querySelector('.carousel-prev');
    this.nextButton = section.querySelector('.carousel-next');
    this.dotsContainer = section.querySelector('.carousel-dots');

    this.currentIndex = 0;
    this.totalSlides = this.items.length;

    // Touch/swipe properties
    this.startX = 0;
    this.currentX = 0;
    this.isDragging = false;
    this.startTransform = 0;

    this.init();
  }

  init() {
    if (this.totalSlides === 0) return;

    this.createDots();
    this.attachEventListeners();
    this.updateCarousel();
    this.checkIfMobile();

    // Update on window resize
    window.addEventListener('resize', () => this.checkIfMobile());
  }

  checkIfMobile() {
    this.isMobile = window.innerWidth <= 749;
    if (!this.isMobile) {
      // Reset transform on desktop
      this.grid.style.transform = 'translateX(0)';
      this.currentIndex = 0;
      this.updateCarousel();
    }
  }

  createDots() {
    this.dotsContainer.innerHTML = '';
    for (let i = 0; i < this.totalSlides; i++) {
      const dot = document.createElement('button');
      dot.classList.add('carousel-dot');
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.addEventListener('click', () => this.goToSlide(i));
      this.dotsContainer.appendChild(dot);
    }
    this.dots = this.dotsContainer.querySelectorAll('.carousel-dot');
  }

  attachEventListeners() {
    // Button navigation
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.prevSlide());
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.nextSlide());
    }

    // Touch events for swipe
    this.grid.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
    this.grid.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: false });
    this.grid.addEventListener('touchend', (e) => this.handleTouchEnd(e));

    // Mouse events for desktop drag (optional)
    this.grid.addEventListener('mousedown', (e) => this.handleMouseDown(e));
    this.grid.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    this.grid.addEventListener('mouseup', (e) => this.handleMouseUp(e));
    this.grid.addEventListener('mouseleave', (e) => this.handleMouseUp(e));

    // Keyboard navigation
    this.section.addEventListener('keydown', (e) => this.handleKeyboard(e));
  }

  handleTouchStart(e) {
    if (!this.isMobile) return;
    this.startX = e.touches[0].clientX;
    this.isDragging = true;
    this.startTransform = this.currentIndex * -100;
    this.grid.style.transition = 'none';
  }

  handleTouchMove(e) {
    if (!this.isDragging || !this.isMobile) return;

    e.preventDefault();
    this.currentX = e.touches[0].clientX;
    const diff = this.currentX - this.startX;
    const percentage = (diff / this.grid.offsetWidth) * 100;
    const newTransform = this.startTransform + percentage;

    // Add resistance at boundaries
    const maxTransform = 0;
    const minTransform = -(this.totalSlides - 1) * 100;

    if (newTransform > maxTransform) {
      this.grid.style.transform = `translateX(${maxTransform + (newTransform - maxTransform) * 0.3}%)`;
    } else if (newTransform < minTransform) {
      this.grid.style.transform = `translateX(${minTransform + (newTransform - minTransform) * 0.3}%)`;
    } else {
      this.grid.style.transform = `translateX(${newTransform}%)`;
    }
  }

  handleTouchEnd(e) {
    if (!this.isDragging || !this.isMobile) return;

    this.isDragging = false;
    this.grid.style.transition = 'transform 0.3s ease';

    const diff = this.currentX - this.startX;
    const threshold = this.grid.offsetWidth * 0.2; // 20% of width

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.prevSlide();
      } else {
        this.nextSlide();
      }
    } else {
      this.updateCarousel();
    }
  }

  handleMouseDown(e) {
    if (!this.isMobile) return;
    e.preventDefault();
    this.startX = e.clientX;
    this.isDragging = true;
    this.startTransform = this.currentIndex * -100;
    this.grid.style.transition = 'none';
    this.grid.style.cursor = 'grabbing';
  }

  handleMouseMove(e) {
    if (!this.isDragging || !this.isMobile) return;

    this.currentX = e.clientX;
    const diff = this.currentX - this.startX;
    const percentage = (diff / this.grid.offsetWidth) * 100;
    const newTransform = this.startTransform + percentage;

    this.grid.style.transform = `translateX(${newTransform}%)`;
  }

  handleMouseUp(e) {
    if (!this.isDragging || !this.isMobile) return;

    this.isDragging = false;
    this.grid.style.transition = 'transform 0.3s ease';
    this.grid.style.cursor = 'grab';

    const diff = this.currentX - this.startX;
    const threshold = this.grid.offsetWidth * 0.2;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.prevSlide();
      } else {
        this.nextSlide();
      }
    } else {
      this.updateCarousel();
    }
  }

  handleKeyboard(e) {
    if (!this.isMobile) return;

    if (e.key === 'ArrowLeft') {
      this.prevSlide();
    } else if (e.key === 'ArrowRight') {
      this.nextSlide();
    }
  }

  prevSlide() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateCarousel();
    }
  }

  nextSlide() {
    if (this.currentIndex < this.totalSlides - 1) {
      this.currentIndex++;
      this.updateCarousel();
    }
  }

  goToSlide(index) {
    this.currentIndex = index;
    this.updateCarousel();
  }

  updateCarousel() {
    // Update transform
    const translateX = this.currentIndex * -100;
    this.grid.style.transform = `translateX(${translateX}%)`;

    // Update dots
    if (this.dots) {
      this.dots.forEach((dot, index) => {
        dot.classList.toggle('active', index === this.currentIndex);
      });
    }

    // Update buttons
    if (this.prevButton) {
      this.prevButton.disabled = this.currentIndex === 0;
    }
    if (this.nextButton) {
      this.nextButton.disabled = this.currentIndex === this.totalSlides - 1;
    }
  }
}

// Initialize carousels when DOM is ready
function initThreeColumnCarousels() {
  const sections = document.querySelectorAll('.three-column-images-section');
  sections.forEach(section => {
    new ThreeColumnCarousel(section);
  });
}

// Initialize on load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initThreeColumnCarousels);
} else {
  initThreeColumnCarousels();
}

// Re-initialize when Shopify section is rendered (for theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    if (event.target.classList.contains('section-three-column-images')) {
      initThreeColumnCarousels();
    }
  });
}
